import type { QueryResult } from '@apollo/client';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import useIsomorphicRedirect, {
  RedirectStatusCode
} from '@aurora/shared-client/components/useIsomorphicRedirect';
import useLoginRedirect from '@aurora/shared-client/components/useLoginRedirect';
import useRegistrationStatus from '@aurora/shared-client/components/users/useRegistrationStatus';
import { canViewSalesforceCasePortal } from '@aurora/shared-client/helpers/nodes/NodePolicyHelper';
import type { RouteWithQuery } from '@aurora/shared-client/routes/useCustomRouter';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import { NodeType } from '@aurora/shared-types/nodes/enums';
import { EndUserPages, EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import {
  AccessFunctionType,
  PageAccessRequiredContextObject,
  RestrictedPageBehavior
} from '@aurora/shared-types/redirects/pageRedirect';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import { useContext } from 'react';
import type {
  AppContextQuery,
  AppContextQueryVariables,
  ContextMessageQuery,
  ContextMessageQueryVariables,
  ContextNodeQuery,
  ContextNodeQueryVariables,
  ContextUserQuery,
  ContextUserQueryVariables
} from '../types/graphql-types';
import ActionFeedbacks from './common/ActionFeedback/ActionFeedbackTypes';
import useEditPageAccess from './useEditPageAccess';
import useEndUserPageDescriptor from './useEndUserPageDescriptor';
import useGroupHubPageAccess from './useGroupHubPageAccess';
import useLocalizedCategoriesAccess from './useLocalizedCategoriesAccess';
import useManageUserPageAccess from './useManageUserPageAccess';
import useMaintenancePageAccess from './useMaintenancePageAccess';
import { useIsPreviewMode } from './messages/useCurrentOrPreviewMessage';

export interface PageAccessData {
  /**
   * The app context query result from the AppContextProvider
   */
  appContextQueryResult: Pick<
    QueryResult<AppContextQuery, AppContextQueryVariables>,
    'loading' | 'data'
  >;
  /**
   * The context node query result from the AppContextProvider
   */
  contextNodeQueryResult: Pick<
    QueryResult<ContextNodeQuery, ContextNodeQueryVariables>,
    'loading' | 'data'
  >;
  /**
   * The context message query result from the AppContextProvider
   */
  contextMessageQueryResult: Pick<
    QueryResult<ContextMessageQuery, ContextMessageQueryVariables>,
    'loading' | 'data'
  >;
  /**
   * The context user query result from the AppContextProvider
   */
  contextUserQueryResult: Pick<
    QueryResult<ContextUserQuery, ContextUserQueryVariables>,
    'loading' | 'data'
  >;
}

/**
 * Redirects a user when they cannot access a given page, due to either not being logged in, or the context object
 * returning a null value.
 *
 * A null value for the given context object may indicate that the object does not exist, or, policies are in place
 * restricting the user from access
 *
 * @param pageAccessData query results made from the AppContextProvider
 * @param isAnonymous whether the user is logged in
 * @param communityId the id of the community
 * @param redirectOnContextMessageArchived whether to redirect user when context message is not accessible
 */
export default function usePageAccess(
  pageAccessData: PageAccessData,
  isAnonymous: boolean,
  communityId: string,
  redirectOnContextMessageArchived: boolean = true
): { loading: boolean } {
  const { router } = useEndUserRoutes();
  const categoryId = router.getUnwrappedQueryParam(EndUserQueryParams.CATEGORY_ID);
  const parentId = categoryId ? `${NodeType.CATEGORY}:${categoryId}` : communityId;

  const {
    redirectBehavior: { restrictedPageBehavior, pageAccessResolver }
  } = useEndUserPageDescriptor();
  const {
    publicConfig: { salesforceIntegrationEnabled, casePortalEnabled }
  } = useContext(TenantContext);
  const { confirmEmailStatus } = useRegistrationStatus();
  const { requiredContextObject, resolverFunctionType } = pageAccessResolver;

  const { contextNodeQueryResult, contextMessageQueryResult, appContextQueryResult } =
    pageAccessData;
  const { loading: contextNodeLoading, data: contextNodeQueryData } = contextNodeQueryResult;
  const isContextNodeReady: boolean = !contextNodeLoading && !!contextNodeQueryData;
  const isContextNodeNull: boolean = isContextNodeReady && !contextNodeQueryData.coreNode;

  const { data: nodePoliciesData, loading: nodePoliciesLoading } = appContextQueryResult;
  const isNodePolicyReady: boolean = !nodePoliciesLoading && !!nodePoliciesData;

  const { loading: contextMessageLoading, data: contextMessageQueryData } =
    contextMessageQueryResult;
  const isContextMessageLoading: boolean = !contextMessageQueryData && contextMessageLoading;
  const isContextMessageNull: boolean =
    !isContextMessageLoading && !contextMessageQueryData?.message;

  const { isLocalizedCategoriesAccessReady, isLocalizedCategoriesFeatureEnabled, routeData } =
    useLocalizedCategoriesAccess(resolverFunctionType);
  const shouldLocalizedCategoriesRedirect: boolean = isLocalizedCategoriesAccessReady
    ? isLocalizedCategoriesFeatureEnabled && !!routeData
    : true;

  const { isEditPageAccessReady, canAccessEditPage } = useEditPageAccess(
    resolverFunctionType,
    contextNodeQueryData?.coreNode,
    contextMessageQueryResult
  );
  const { isGroupPageAccessReady, canAccessGroupPage } = useGroupHubPageAccess(
    resolverFunctionType,
    parentId,
    contextNodeQueryData?.coreNode
  );

  const { isManageUsersPageAccessReady, canAccessManageUsersPage } = useManageUserPageAccess(
    resolverFunctionType,
    isAnonymous
  );
  const { isMaintenancePageAccessReady, canAccessMaintenancePage, canAdminCommunity } =
    useMaintenancePageAccess(
      resolverFunctionType,
      isNodePolicyReady && nodePoliciesData?.community
    );
  const maintenanceEnabled =
    (isNodePolicyReady &&
      nodePoliciesData?.community?.maintenanceProperties?.isMaintenanceModeEnabled) ??
    false;
  const currentPage = router.getCurrentPageName();
  const isReady =
    isEditPageAccessReady &&
    isGroupPageAccessReady &&
    isManageUsersPageAccessReady &&
    isMaintenancePageAccessReady;
  const isPreviewMode: boolean = useIsPreviewMode();

  let redirectRoute: RouteWithQuery<EndUserPages, EndUserQueryParams> = {
    route: EndUserPages.CommunityPage,
    params: {}
  };

  switch (restrictedPageBehavior) {
    case RestrictedPageBehavior.COMMUNITY_PAGE_WITH_ALERT_OR_LOGIN_PAGE: {
      if (resolverFunctionType === AccessFunctionType.CREATE_GROUP_HUB_PAGE && routeData) {
        redirectRoute = {
          route: routeData.route,
          params: routeData.params,
          query: routeData.query
        };
      } else {
        redirectRoute = {
          route: EndUserPages.CommunityPage,
          params: {},
          query: {
            [EndUserQueryParams.ACTION_FEEDBACK]: ActionFeedbacks.PAGE_NOT_FOUND,
            [EndUserQueryParams.TIMESTAMP]: Date.now().toString()
          }
        };
      }
      break;
    }
    case RestrictedPageBehavior.COMMUNITY_PAGE_OR_LOGIN_PAGE: {
      redirectRoute = {
        route: EndUserPages.CommunityPage,
        params: null,
        query: null
      };
      break;
    }
    case RestrictedPageBehavior.LOCALIZED_CATEGORIES_PAGE: {
      if (routeData) {
        redirectRoute = {
          route: routeData.route,
          params: routeData.params,
          query: routeData.query
        };
      }

      break;
    }

    default:
  }

  let shouldRedirect: boolean = false;

  switch (requiredContextObject) {
    case PageAccessRequiredContextObject.NODE: {
      shouldRedirect = isContextNodeReady && isContextNodeNull;
      break;
    }
    case PageAccessRequiredContextObject.MESSAGE: {
      shouldRedirect =
        (!isContextMessageLoading && isContextMessageNull) || (isAnonymous && isPreviewMode);
      break;
    }
    case PageAccessRequiredContextObject.USER: {
      shouldRedirect = isAnonymous;
      break;
    }
    default: {
      break;
    }
  }
  if (
    isNodePolicyReady &&
    isMaintenancePageAccessReady &&
    maintenanceEnabled &&
    !canAdminCommunity &&
    currentPage != EndUserPages.MaintenancePage &&
    currentPage != EndUserPages.LoginPage &&
    currentPage != EndUserPages.VerifyUserEmailPage
  ) {
    shouldRedirect = true;
    redirectRoute = {
      route: EndUserPages.MaintenancePage,
      params: {},
      query: {}
    };
  }

  if (!shouldRedirect && resolverFunctionType) {
    switch (resolverFunctionType) {
      case AccessFunctionType.POST_PAGE: {
        shouldRedirect =
          isContextNodeReady && !contextNodeQueryData.coreNode.userContext.canPostMessages;
        break;
      }
      case AccessFunctionType.CREATE_GROUP_HUB_PAGE: {
        shouldRedirect =
          shouldLocalizedCategoriesRedirect || (isGroupPageAccessReady && !canAccessGroupPage);
        break;
      }
      case AccessFunctionType.POST_GROUP_HUB_PAGE: {
        shouldRedirect = isGroupPageAccessReady && !canAccessGroupPage;
        break;
      }
      case AccessFunctionType.EDIT_GROUP_HUB_PAGE: {
        shouldRedirect = isContextNodeReady && isGroupPageAccessReady && !canAccessGroupPage;
        break;
      }
      case AccessFunctionType.MANAGE_MEMBERS_PAGE: {
        shouldRedirect = isContextNodeReady && isGroupPageAccessReady && !canAccessGroupPage;
        break;
      }
      case AccessFunctionType.MANAGE_USERS_PAGE: {
        shouldRedirect = isManageUsersPageAccessReady && !canAccessManageUsersPage;
        break;
      }
      case AccessFunctionType.CASE_PORTAL: {
        shouldRedirect =
          isNodePolicyReady &&
          !(
            (salesforceIntegrationEnabled && casePortalEnabled) ||
            (confirmEmailStatus && canViewSalesforceCasePortal(nodePoliciesData?.community))
          );
        break;
      }
      case AccessFunctionType.INBOX_PAGE: {
        shouldRedirect =
          isNodePolicyReady &&
          !checkPolicy(nodePoliciesData?.community?.communityPolicies?.canUsePrivateNotes);
        break;
      }
      case AccessFunctionType.NOTIFICATIONS_PAGE: {
        shouldRedirect =
          isNodePolicyReady &&
          !checkPolicy(nodePoliciesData?.community?.communityPolicies?.canUseNotifications);
        break;
      }
      case AccessFunctionType.EDIT_PAGE:
      case AccessFunctionType.CONTENT_WORKFLOW_EDIT_PAGE: {
        shouldRedirect =
          isContextNodeReady &&
          !isContextMessageLoading &&
          isEditPageAccessReady &&
          !canAccessEditPage;
        break;
      }
      case AccessFunctionType.LOCALIZED_CATEGORIES_PAGE:
      case AccessFunctionType.GROUPS_PAGE: {
        shouldRedirect = shouldLocalizedCategoriesRedirect;
        break;
      }
      case AccessFunctionType.MAINTENANCE_PAGE: {
        shouldRedirect =
          isNodePolicyReady && isMaintenancePageAccessReady && !canAccessMaintenancePage;
        break;
      }
      case AccessFunctionType.WORKSTREAMS_PAGE: {
        shouldRedirect =
          isAnonymous ||
          (isNodePolicyReady &&
            !checkPolicy(nodePoliciesData?.community?.communityPolicies?.canViewWorkstreams));
        break;
      }
      default: {
        break;
      }
    }
  }

  // redirect the user to the proper category page if localized categories page restricted behavior is set for the page
  // and a category is mapped to the language in use
  useIsomorphicRedirect(shouldRedirect && !!routeData, redirectRoute);

  //maintenance page shouldn't redirect to login page for anonymous users
  const shouldRedirectLoginPage =
    !shouldLocalizedCategoriesRedirect &&
    shouldRedirect &&
    isAnonymous &&
    redirectOnContextMessageArchived &&
    !maintenanceEnabled;

  const shouldRedirectLoggedIn =
    shouldRedirect &&
    !shouldLocalizedCategoriesRedirect &&
    redirectOnContextMessageArchived &&
    (!isAnonymous ||
      resolverFunctionType == AccessFunctionType.MAINTENANCE_PAGE ||
      maintenanceEnabled);

  // redirect the user to the login page if they are anonymous and there is restricted behavior is set for the page,
  //  and no context object is present
  useLoginRedirect(shouldRedirectLoginPage);

  // redirect logged-in users to the page specified for the page, given that no context object is present
  useIsomorphicRedirect(
    shouldRedirectLoggedIn,
    redirectRoute,
    RedirectStatusCode.MOVED_TEMPORARILY
  );

  return {
    loading: !(
      isReady &&
      !shouldLocalizedCategoriesRedirect &&
      !shouldRedirectLoginPage &&
      !shouldRedirectLoggedIn
    )
  };
}
